import type { TrackedProduct } from '@/domains/shoppingCart/common/interfaces/TrackedProduct';
import type { TrackedProductDetails } from '@/domains/shoppingCart/common/interfaces/TrackedProductDetails';
import type { ProductDetails } from '@/domains/shoppingCart/interfaces/ProductDetails';
import { isSecondHand } from '@/domains/sustainability/secondHand/utils/secondHand';

import type { PlaylistReconciliation } from '../../AddToCart/AddToCart.actions';

interface ProductDetailsWithThresholdIndex extends ProductDetails {}

interface ToTrackedProductParams {
  product: ProductDetailsWithThresholdIndex | TrackedProductDetails;
  playlistReconciliation?: PlaylistReconciliation;
}

const invalidValue = ['', NaN, undefined, null, typeof true];

const sharedKeys = [
  'eco_label_name',
  'has_eco_label',
  'list_name',
  'order_id',
  'order_ref',
  'pr_attributes_count',
  'pr_base_price',
  'pr_brand_id',
  'pr_brand_name',
  'pr_category_id',
  'pr_category_name',
  'pr_degraded_detail',
  'pr_delivery_mode',
  'pr_family_id',
  'pr_family_name',
  'pr_has_cheapest_delivery_fee',
  'pr_has_cheapest_overall_price',
  'pr_has_coupon_label',
  'pr_has_fastest_delivery',
  'pr_has_promo_label',
  'pr_has_variant',
  'pr_is_degraded',
  'pr_is_mf',
  'pr_is_pro',
  'pr_is_restocking',
  'pr_is_sponsored',
  'pr_is_top_sales',
  'pr_lowest_category_id',
  'pr_lowest_category_name',
  'pr_md_count',
  'pr_md_top',
  'pr_model_id',
  'pr_model_name',
  'pr_model_name',
  'pr_position',
  'pr_price',
  'pr_rating_count',
  'pr_rating',
  'pr_seller_id',
  'pr_shipping_options_count',
  'pr_shipping_options',
  'pr_subcategory_id',
  'pr_subcategory_name',
  'pr_subsubcategory_id',
  'pr_subsubcategory_name',
  'reco_engine_name',
  'search_query',
  'shipping',
  'total_ati',
  'playlist_reconciliation',
  'playlist_fingerprint',
  'pr_technical_attributes',
  'pr_label',
] as const;

export const toTrackedProduct = ({
  product,
  playlistReconciliation,
}: ToTrackedProductParams): TrackedProduct => {
  const trackingObj: TrackedProduct = Object.fromEntries(
    sharedKeys.map((key) => [key, (product as TrackedProductDetails)[key]]),
  );

  trackingObj.pr_is_second_hand_product =
    isSecondHand(product.name) ||
    !!(product as TrackedProductDetails).isSecondHand;

  if ((product as ProductDetails).articleId) {
    trackingObj.pr_id = (product as ProductDetails).articleId;
  }
  if (product.unitPrice) {
    trackingObj.pr_base_price = product.oldUnitPrice;
  }
  if (product.unitPrice) {
    trackingObj.pr_price = product.unitPrice;
  }
  if (product.brandName) {
    trackingObj.pr_brand_name = product.brandName;
  }

  trackingObj.pr_is_mf = product.isDeliveredByManomano;

  if ((product as ProductDetails).modelId) {
    trackingObj.pr_model_id = (product as ProductDetails).modelId;
  }

  if (product.id) {
    trackingObj.pr_product_id = product.id;
  }
  if (product.name) {
    trackingObj.pr_name = product.name;
    trackingObj.pr_model_name = product.name;
  }
  if (!invalidValue.includes(product.stockSize)) {
    trackingObj.pr_stock = product.stockSize ? 'available' : 'out of stock';
  }
  if (product.sellerName) {
    trackingObj.pr_seller_name = product.sellerName;
  }
  if ((product as ProductDetails).sellerId) {
    trackingObj.pr_seller_id = (product as ProductDetails).sellerId;
  }

  if (!invalidValue.includes(product.quantity)) {
    trackingObj.pr_quantity = product.quantity;
  }

  if (product.b2b !== undefined) {
    trackingObj.pr_is_pro = product.b2b;
  }

  if (playlistReconciliation?.recoEngineName) {
    trackingObj.reco_engine_name = playlistReconciliation?.recoEngineName;
  }

  trackingObj.playlist_reconciliation =
    (playlistReconciliation && playlistReconciliation.playlistReconciliation) ||
    undefined;
  trackingObj.playlist_fingerprint =
    (playlistReconciliation && playlistReconciliation.playlistFingerprint) ||
    undefined;
  trackingObj.has_a_coupon = !!(product as ProductDetails).coupons?.length;
  trackingObj.coupon_amount = (product as ProductDetails).coupons?.length
    ? (product as ProductDetails).coupons?.[0].amount
    : undefined;
  trackingObj.pr_is_in_promo = (
    product as TrackedProductDetails
  ).pr_is_in_promo;

  return trackingObj;
};
